import * as Yup from 'yup';
import { Grid } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { ActiveDataRoom, DataRoomFolder } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

interface CopyDocumentsDrawerProps {
	dataRoomGuid: string | undefined;
	activeDeals: ActiveDataRoom[] | undefined;
	folder: DataRoomFolder | null;
	close: () => void;
}

export const CopyDocumentsDrawer: React.FC<CopyDocumentsDrawerProps> = ({
	dataRoomGuid,
	activeDeals,
	folder,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();

	const copyFolderMutation = useMutation(
		async ({ values }: CopyDocumentsFormMutationVariables) => {
			const { data, error } = await getApiResponseData<void>(
				api.post(CerebroApiEndpoints.DataRooms.CopyDocuments(dataRoomGuid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Data room documents were successfully copied.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={folder != null} onClose={close}>
			<CerebroHeading component="h1" size="h1">
				Copy Documents to Data Room
			</CerebroHeading>

			<Formik
				validationSchema={CopySchema}
				initialValues={{
					destinationGuid: '',
				}}
				onSubmit={FormikUtils.MutateOnSubmit(copyFolderMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroText sx={{ marginBottom: 3 }}>
										Select the destination deal that you would like to copy all files and folders
										from this data room over to. All files and folders will be copied over. No
										permissions will be copied, those will need to be setup manually in the
										destination data room.
									</CerebroText>
								</Grid>
								<Grid item xs={11}>
									<CerebroLabel>Destination Deal</CerebroLabel>
									<Field
										id="destinationGuid"
										as={CerebroDropdown}
										name="destinationGuid"
										disabled={isSubmitting}
									>
										{activeDeals?.map(deal => (
											<CerebroDropdownMenuItem key={deal.guid} value={deal.guid}>
												{deal.dealName}
											</CerebroDropdownMenuItem>
										))}
									</Field>
									{FormikUtils.IsInvalid('destinationGuid', errors, touched) && (
										<CerebroText variant="error">{errors.destinationGuid}</CerebroText>
									)}
								</Grid>
								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Copy
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type CopyDocumentsFormMutationVariables = FormikUtils.MutateOnSubmitVariables<CopyDocumentsFormValues>;
interface CopyDocumentsFormValues {
	destinationGuid: string;
}
type CopyDocumentsFormValueErrors = FormikErrors<CopyDocumentsFormValues>;

const CopySchema = Yup.object().shape({
	destinationGuid: Yup.string().required('Destination data room must be selected.'),
});
